import { useState, useRef } from "react";
import {
  IconMap2,
  IconLock,
  IconLockOpen,
  IconStar,
  IconWind,
  IconRefreshAlert,
  IconScale,
  IconUsersGroup,
  IconMessage2,
  IconTargetArrow,
  IconLighter,
  IconWindOff,
} from "@tabler/icons-react";
import styles from "../../../../pages/TalentDashboard/Profile/profile.module.css";
import Slider from "react-slick";
import { careerAttempts, careerPlanUnlockPrice } from "./data";

const IMPROVE_ICONS = {
  wind: IconWind,
  refreshAlert: IconRefreshAlert,
  scale: IconScale,
  usersGroup: IconUsersGroup,
  message2: IconMessage2,
  targetArrow: IconTargetArrow,
};

export function CourseThumb() {
  return (
    <div className={styles.courseThumb}>
      <IconStar size={12} className={styles.courseThumbStar} />
      <div className={`${styles.courseThumbLine} ${styles.courseThumbLine1}`}>
        LEARN.
      </div>
      <div className={`${styles.courseThumbLine} ${styles.courseThumbLine2}`}>
        IMPROVE.
      </div>
      <div className={`${styles.courseThumbLine} ${styles.courseThumbLine3}`}>
        LEVEL UP.
      </div>
    </div>
  );
}

export default function CareerPlanTab({
  data,
  isUnlock,
  attempts = careerAttempts,
  unlockPrice = careerPlanUnlockPrice,
  setDisclaimerModal,
}) {
  const [index, setIndex] = useState(0);
  const [slideClass, setSlideClass] = useState("");
  const [pulsing, setPulsing] = useState(false);
  const animatingRef = useRef(false);
  const [openModal, setOpenModal] = useState(false);
  const timersRef = useRef([]);

  function goToAttempt(newIndex) {
    if (animatingRef.current || newIndex === index) return;
    animatingRef.current = true;

    const direction = newIndex > index ? "next" : "prev";
    setSlideClass(
      direction === "next" ? styles.slideOutNext : styles.slideOutPrev,
    );

    timersRef.current.push(
      setTimeout(() => {
        setIndex(newIndex);
        setSlideClass(
          direction === "next" ? styles.slideInNext : styles.slideInPrev,
        );

        timersRef.current.push(
          setTimeout(() => {
            setSlideClass("");
            timersRef.current.push(
              setTimeout(() => {
                animatingRef.current = false;
              }, 340),
            );
          }, 20),
        );
      }, 320),
    );
  }

  function handleUnlock() {
    // setUnlocked(true);
    setDisclaimerModal(true);
    setOpenModal(false);
    setPulsing(true);
    timersRef.current.push(setTimeout(() => setPulsing(false), 500));
  }

  const attempt = attempts[index];

  const totalCourses =
    data?.length > 0
      ? data?.reduce(
          (acc, skill) => acc + (skill?.resources?.length || 0),
          0,
        ) || 0
      : 0;
  const improvementData = Array.isArray(data) ? data : [];
  const slides = Array.isArray(data)
    ? data.filter((item) => item?.testResult?.skillsAnalysis?.length > 0)
    : [];

  const settings = {
    dots: slides?.length > 1,
    infinite: slides?.length > 1,
    autoplay: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIcon}>
            <IconMap2 size={18} />
          </div>
          <div>
            <div className={styles.cardTitle}>Career Plan</div>
            <div className={styles.cardSubtitle}>
              Improvement tips and a learning plan from each of your test
              attempts
            </div>
          </div>
        </div>
      </div>
      <div className={styles.cardBody}>
        <div className={styles.lockedWrap}>
          <div
            className={`${styles.lockedContent} ${isUnlock ? styles.lockedContentUnlocked : ""} ${pulsing ? styles.unlockPulse : ""}`}
          >
            <div className={styles.attemptViewport}>
              {isUnlock && (
                <Slider {...settings}>
                  {slides?.map((item, index) => (
                    <div key={index}>
                      {item?.testResult?.skillsAnalysis?.map((skill, idx) => (
                        <div key={index} className="mb-6">
                          <h3 className="mb-2 font-semibold">{skill?.skill}</h3>

                          <div className="lg:grid grid-cols-1 gap-x-2 py-4">
                            {skill?.summary && (
                              <div className={styles.improveRow}>
                                <div className={styles.improveIcon}>
                                  <IconWindOff size={15} />
                                </div>
                                <div>
                                  <div className={styles.improveTitle}>
                                    Summary
                                  </div>
                                  <div className={styles.improveTip}>
                                    {skill?.summary}
                                  </div>
                                </div>
                              </div>
                            )}

                            {skill?.weaknesses?.length > 0 && (
                              <div className={styles.improveRow}>
                                <div className={styles.improveIcon}>
                                  <IconRefreshAlert size={15} />
                                </div>
                                <div>
                                  <div className={styles.improveTitle}>
                                    Weaknesses
                                  </div>
                                  <div className={styles.improveTip}>
                                    {skill?.weaknesses?.join(", ")}
                                  </div>
                                </div>
                              </div>
                            )}
                            {skill?.strengths?.length > 0 && (
                              <div className={styles.improveRow}>
                                <div className={styles.improveIcon}>
                                  <IconLighter size={15} />
                                </div>
                                <div>
                                  {" "}
                                  <div className={styles.improveTitle}>
                                    Strengths
                                  </div>
                                  <div className={styles.improveTip}>
                                    {skill?.strengths?.join(", ")}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {skill?.improvementAreas?.length > 0 && (
                            <div className="mb-8">
                              {skill?.improvementAreas?.map((area, idx) => (
                                <div key={idx} className={styles.improveRow}>
                                  <span className={styles.improveTitle}>
                                    {area?.area}
                                  </span>
                                  <span className={styles.improveTip}>
                                    {area?.reason}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}

                          {Array.isArray(skill?.resources) &&
                            skill?.resources?.map((item, idx) => (
                              <div
                                className={`${styles.courseRow} ${idx === 0 ? styles.courseRowFirst : ""}`}
                                key={item?.title}
                                onClick={() =>
                                  window.open(
                                    item?.url,
                                    "_blank",
                                    "noopener,noreferrer",
                                  )
                                }
                              >
                                <CourseThumb />
                                <div className={styles.courseInfo}>
                                  <div className={styles.courseTitle}>
                                    {item?.title}
                                  </div>
                                  <div className={styles.courseDesc}>
                                    {item?.description}
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </Slider>
              )}

              {!isUnlock && (
                <div className={`${styles.attemptSlide} ${slideClass}`}>
                  <div className={styles.attemptHead}>
                    <div>
                      <div className={styles.attemptLabel}>Test Attempt</div>
                      <div className={styles.attemptDate}>{attempt.date}</div>
                    </div>
                    <div className={styles.attemptScore}>
                      Score <b>{attempt.score}%</b>
                    </div>
                  </div>

                  <div
                    className={`${styles.sectionLabel} ${styles.sectionLabelFirst}`}
                  >
                    What to Improve On
                  </div>
                  <div>
                    {attempt.improvements.map((item, i) => {
                      const Icon = IMPROVE_ICONS[item.icon];
                      return (
                        <div
                          className={`${styles.improveRow} ${i === 0 ? styles.improveRowFirst : ""}`}
                          key={item.title}
                        >
                          <div className={styles.improveIcon}>
                            <Icon size={15} />
                          </div>
                          <div>
                            <div className={styles.improveTitle}>
                              {item.title}
                            </div>
                            <div className={styles.improveTip}>{item.tip}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className={styles.sectionLabel}>Recommended Courses</div>
                  <div>
                    {attempt.courses.map((c, i) => (
                      <div
                        className={`${styles.courseRow} ${i === 0 ? styles.courseRowFirst : ""}`}
                        key={c.title}
                      >
                        <CourseThumb />
                        <div className={styles.courseInfo}>
                          <div className={styles.courseTitle}>{c.title}</div>
                          <div className={styles.courseDesc}>{c.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {!isUnlock && (
              <div className={styles.attemptDots}>
                {attempts.map((a, i) => (
                  <button
                    key={a.date}
                    type="button"
                    className={`${styles.attemptDot} ${i === index ? styles.attemptDotActive : ""}`}
                    aria-label={`Attempt ${i + 1}`}
                    onClick={() => goToAttempt(i)}
                  />
                ))}
              </div>
            )}
          </div>

          {!isUnlock && (
            <div
              className={`${styles.unlockOverlay} ${isUnlock ? styles.unlockOverlayHidden : ""}`}
            >
              <div className={styles.unlockPanel}>
                <div className={styles.unlockIcon}>
                  <IconLock size={20} />
                </div>
                <div className={styles.unlockTitle}>Unlock Career Plan</div>
                <div className={styles.unlockText}>
                  See your personalized improvement plan and learning path.
                </div>
                <div className={styles.unlockPrice}>{unlockPrice}</div>
                <button
                  type="button"
                  className={styles.unlockBtn}
                  onClick={handleUnlock}
                >
                  <IconLockOpen size={15} /> Unlock Now
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
